import { Injectable } from '@angular/core';
import { SyncServerServiceProvider } from '../sync-server-service/sync-server-service';
import { Utility } from '../util-service/util-service';
import * as _ from 'lodash';

const HISTORY_SAMPLE_SIZE = 50;

/**
 * Resolves which parent ("district") lab to default for the current user.
 *
 * The chain is three deep on purpose, and only the last link is implemented.
 *
 * The unit a user belongs to is the thing actually being modelled here. LADOTD's ProjectOffice
 * rows are every one of them named `District NN Gang XXX`, and `User.IDProjectOffice` is the
 * association — so the durable answer is for a user's unit to carry the default. That association
 * is populated for roughly half of recently active users and its coverage is falling, and there is
 * no District entity at all, so the unit cannot be the source of truth yet. Waiting for it is not
 * an option either: the selector is painful today.
 *
 * Inference from the user's own history carries the load in the meantime. It needs no data we do
 * not already have, and the parent lab a user last worked with predicts their next one about four
 * times in five — far better than the parent-agnostic defaults it displaces.
 *
 * `getStoredParentLabID` and `getUnitParentLabID` are the seams for that future and are
 * intentionally unimplemented. Filling either should not require touching the call site.
 */
@Injectable()
export class LabDefaultService
{
    inferredParentLabID: number = null;
    habitualLabIDs: number[] = null;
    private inferInFlight: Promise<number> = null;

    constructor(private syncService: SyncServerServiceProvider, private utility: Utility)
    {
    }

    /**
     * The district keys the user habitually works with, most-used first.
     *
     * A district key is a root lab's own IDLab, or a child lab's IDLabParent - the entity the user
     * actually selects in the picker. Used to break ties when the sampling plan allows a test to go
     * to more than one lab; it never adds labs the plan does not ask for, so a heavy history cannot
     * drag the selection back towards "everything".
     *
     * @return {Promise<Array<number>>} District keys, most-used first. Empty when there is no usable history.
     */
    async getHabitualLabIDs(): Promise<number[]>
    {
        await this.inferParentLabIDFromHistory();
        return this.habitualLabIDs || [];
    }

    /**
     * The parent lab to pre-select for the current user, or 0 when nothing can be resolved.
     *
     * @return {Promise<number>} An IDLab of a parent lab, or 0.
     */
    async getDefaultParentLabID(): Promise<number>
    {
        const tmpStored = await this.getStoredParentLabID();
        if (tmpStored)
        {
            return tmpStored;
        }
        const tmpUnit = await this.getUnitParentLabID();
        if (tmpUnit)
        {
            return tmpUnit;
        }
        return await this.inferParentLabIDFromHistory();
    }

    /**
     * A lab the user has explicitly pinned. Not yet stored anywhere.
     *
     * @return {Promise<number>} Always 0 until a preference store exists.
     */
    async getStoredParentLabID(): Promise<number>
    {
        return 0;
    }

    /**
     * The lab implied by the user's unit. Awaiting `User.IDProjectOffice` coverage and a lab to
     * district association; neither is dependable enough to read from yet.
     *
     * @return {Promise<number>} Always 0 until the unit is modelled.
     */
    async getUnitParentLabID(): Promise<number>
    {
        return 0;
    }

    /**
     * The parent lab the user has most often sent recent tests to.
     *
     * Modal rather than strictly most-recent: a single errand to another district should not
     * redirect every subsequent sample. Resolved once per session — a user's own history does not
     * meaningfully move while they are working.
     *
     * @return {Promise<number>} An IDLab of a parent lab, or 0 when there is no usable history.
     */
    async inferParentLabIDFromHistory(): Promise<number>
    {
        if (this.inferredParentLabID !== null)
        {
            return this.inferredParentLabID;
        }
        // Serialised rather than guarded by the cache field: several pickers resolve this at once on
        // a fresh form, and publishing the result only on success keeps a transient failure from
        // being cached for the rest of the session.
        if (!this.inferInFlight)
        {
            this.inferInFlight = this.resolveParentLabIDFromHistory().then((pResult) =>
            {
                if (pResult)
                {
                    this.inferredParentLabID = pResult;
                }
                this.inferInFlight = null;
                return pResult;
            }, () =>
            {
                this.inferInFlight = null;
                return 0;
            });
        }
        return await this.inferInFlight;
    }

    /**
     * The uncached history lookup behind inferParentLabIDFromHistory.
     *
     * @return {Promise<number>} An IDLab of a parent lab, or 0 when there is no usable history.
     */
    private async resolveParentLabIDFromHistory(): Promise<number>
    {
        const tmpIDUser = this.utility.UserId();
        if (!tmpIDUser)
        {
            return 0;
        }
        const tmpQuery = `/FilteredTo/FBV~CreatingIDUser~EQ~${ tmpIDUser }~FSF~IDTestInstance~DESC~0/0/${ HISTORY_SAMPLE_SIZE }`;
        const tmpInstances = await this.syncService.gopher(tmpQuery, 'TestInstances', 'TestInstances:LabDefault');
        if (!Array.isArray(tmpInstances) || !tmpInstances.length)
        {
            return 0;
        }
        const tmpLabIDs = _.uniq(_.filter(_.map(tmpInstances, (pRow: any) => Number(pRow.IDLab)), Boolean));
        if (!tmpLabIDs.length)
        {
            return 0;
        }
        // Distinct scope: the lab picker resolves its own labs through the same endpoint while this
        // is in flight, and a shared key cancels one of them without settling it.
        const tmpLabs = await this.syncService.getLabsByIDs(`FBL~IDLab~INN~${ tmpLabIDs.join(',') }`, 'getLabsByIDs:LabDefault');
        if (!Array.isArray(tmpLabs) || !tmpLabs.length)
        {
            return 0;
        }
        const tmpParentByLab = {};
        _.each(tmpLabs, (pLab: any) =>
        {
            tmpParentByLab[Number(pLab.IDLab)] = Number(pLab.IDLabParent) > 0 ? Number(pLab.IDLabParent) : Number(pLab.IDLab);
        });
        const tmpCounts = {};
        _.each(tmpInstances, (pRow: any) =>
        {
            const tmpParent = tmpParentByLab[Number(pRow.IDLab)];
            if (tmpParent)
            {
                tmpCounts[tmpParent] = (tmpCounts[tmpParent] || 0) + 1;
            }
        });
        const tmpRanked = _.sortBy(Object.keys(tmpCounts), (pKey) => -tmpCounts[pKey]);
        this.habitualLabIDs = _.map(tmpRanked, Number);
        return tmpRanked.length ? Number(tmpRanked[0]) : 0;
    }
}



// WEBPACK FOOTER //
// ./src/providers/lab-default-service/lab-default-service.ts